import type { ReactNode } from "react";
import { formatMinutes } from "@/components/entry/RuntimeChip.tsx";
import {
  formatDays,
  formatHours,
  formatHoursPerDay,
  formatHoursPerWeek,
} from "@/lib/countdown/format.ts";
import type { LegPace, Remedy } from "@/lib/countdown/legs.ts";
import { setFilters } from "@/state/filters.ts";
import { useSettingsStore } from "@/state/settings.ts";
import styles from "./countdown.module.css";

/**
 * When a leg can't be done at the stated pace, say so plainly and show what
 * would fix it.
 *
 * Two rules this follows:
 *  - **No euphemism in the headline.** "Leg 1 doesn't fit" — not "consider
 *    adjusting your plan". The numbers that make it true come right after.
 *  - **The fixes are buttons, not advice.** Every remedy the app can apply
 *    itself is a button that applies it; the one it can't (cutting titles is
 *    an editorial choice) is stated as the size of the cut, and points at the
 *    filters. A remedy that closes the gap on its own says so — the rest are
 *    honest about only helping.
 */
export function InfeasibleCallout({ leg, legLabel }: { leg: LegPace; legLabel: string }) {
  const setHoursPerWeek = useSettingsStore((state) => state.setHoursPerWeek);
  const setEncoreMode = useSettingsStore((state) => state.setEncoreMode);

  const why =
    leg.daysLeft === 0
      ? `${leg.remainingCount} stops (${formatMinutes(leg.remainingMinutes)}) are still unwatched and the deadline is here.`
      : `${formatMinutes(leg.remainingMinutes)} left across ${leg.remainingCount} stops, ${formatDays(
          leg.daysLeft,
        )} to go. That's ${formatHoursPerDay(leg.requiredHoursPerDay)} h/day, and you've budgeted ${formatHoursPerDay(
          leg.availableHoursPerDay,
        )}.`;

  // No role or aria-label on the wrapper: the first line already says which
  // leg this is about, so a group label would only repeat it.
  return (
    <div className={styles.callout}>
      <p className={styles.calloutHead}>
        <strong>{legLabel} doesn't fit.</strong> {why}
      </p>
      <ul className={styles.remedies}>
        {leg.remedies.map((remedy) => (
          <li key={remedy.kind} className={styles.remedy}>
            <RemedyAction
              remedy={remedy}
              onCompress={() => setFilters((prev) => ({ ...prev, useAlternatives: true }))}
              onRaisePace={(hours) => setHoursPerWeek(hours)}
              onLeaveEncore={() => setEncoreMode(false)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function RemedyAction({
  remedy,
  onCompress,
  onRaisePace,
  onLeaveEncore,
}: {
  remedy: Remedy;
  onCompress: () => void;
  onRaisePace: (hours: number) => void;
  onLeaveEncore: () => void;
}) {
  switch (remedy.kind) {
    case "compress":
      return (
        <RemedyButton sufficient={remedy.sufficient} onClick={onCompress}>
          Take the compressed routes
          <Saves>saves {formatMinutes(remedy.savedMinutes)}</Saves>
        </RemedyButton>
      );
    case "raise-pace":
      return (
        <RemedyButton
          sufficient={remedy.sufficient}
          onClick={() => onRaisePace(remedy.hoursPerWeekNeeded)}
        >
          Watch {formatHoursPerWeek(remedy.hoursPerWeekNeeded)} h/week
          <Saves>up from what you've budgeted</Saves>
        </RemedyButton>
      );
    case "leave-encore":
      return (
        <RemedyButton sufficient={remedy.sufficient} onClick={onLeaveEncore}>
          Turn Encore mode off
          <Saves>{formatDays(remedy.extraDays)} more to work with</Saves>
        </RemedyButton>
      );
    case "trim":
      return (
        <p className={styles.remedyNote}>
          Or cut <strong>{formatHours(remedy.minutes)} hours</strong> from this leg: tighten
          Importance or Quality in the filters.
        </p>
      );
  }
}

function RemedyButton({
  sufficient,
  onClick,
  children,
}: {
  sufficient: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={styles.remedyButton}
      data-sufficient={sufficient}
      onClick={onClick}
      title={sufficient ? "This alone makes the leg fit" : "Helps, but won't close the gap alone"}
    >
      {children}
    </button>
  );
}

function Saves({ children }: { children: ReactNode }) {
  return <span className={styles.remedyDetail}>{children}</span>;
}
